import {
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiResponse } from '@/types/api-types';
import type {
  QueryOptions,
  QuerySuspenseOptions,
} from '@/types/tanstack-query-options';
import type { paths } from '~/generated/auth/api-schema';
import { zodRemoveNullable } from '@/lib/zod-helper';
import { authApi } from '@/services';
import {
  permissionGroupDtoSchema,
  type queryPermissionGroupListQuerySchema,
} from '~/generated/auth/zod-schema';

const API_PATH = '/v1/permissionGroup/queryPermissionGroupList' as const;

export const QUERY_PERMISSION_GROUP_LIST_KEY = API_PATH;

type APIQueryParams = z.infer<typeof queryPermissionGroupListQuerySchema>;

export type { APIQueryParams as QueryPermissionGroupListParams };

const permissionGroupItemSchema = zodRemoveNullable(permissionGroupDtoSchema, {
  excludeMask: {
    modifyDatetime: true,
  },
});
const apiResponseSchema = z.array(permissionGroupItemSchema);

type APIQueryResponse = ApiResponse<paths, typeof API_PATH>;
type APIResponseData = z.infer<typeof apiResponseSchema>;

export type PermissionGroupListItem = z.infer<typeof permissionGroupItemSchema>;

const queryFn = async (
  signal: AbortSignal,
  params?: APIQueryParams,
): Promise<APIResponseData> => {
  const { data: response } = await authApi.get<APIQueryResponse>(API_PATH, {
    signal,
    params,
  });

  return apiResponseSchema.parse(response.data?.permissionGroupList);
};

export const getSuspenseQueryPermissionGroupListOptions = (
  params: APIQueryParams,
) =>
  queryOptions({
    queryKey: [API_PATH, params] as const,
    queryFn: ({ signal }) => queryFn(signal, params),
  });

export const useQueryPermissionGroupList = (
  params?: APIQueryParams,
  options?: QueryOptions<APIResponseData>,
) =>
  useQuery({
    queryKey: [API_PATH, params] as const,
    queryFn: ({ signal }) => queryFn(signal, params),
    ...options,
  });

export const useSuspenseQueryPermissionGroupList = (
  params: APIQueryParams,
  options?: QuerySuspenseOptions<
    APIResponseData,
    readonly [typeof API_PATH, APIQueryParams]
  >,
) =>
  useSuspenseQuery({
    ...getSuspenseQueryPermissionGroupListOptions(params),
    ...options,
  });
